import api from './client';

// League page types
export interface LeagueMatch {
  wyscoutMatchId: number;
  seasonId: number;
  gameweek: number | null;
  dateUtc: string | null;
  status: string;
  label: string | null;
  homeTeamId: number | null;
  awayTeamId: number | null;
  homeTeamName: string | null;
  awayTeamName: string | null;
  homeScore: number | null;
  awayScore: number | null;
  statsSynced: boolean;
}

export interface LeaguePlayerStat {
  playerId: number;
  teamId: number;
  playerName: string | null;
  shortName: string | null;
  position: string | null;
  minutesPlayed: number;
  goals: number;
  assists: number;
  xgShot: number;
  xgAssist: number;
  shots: number;
  keyPasses: number;
  passesAccurate: number;
  passesTotal: number;
  duelsWon: number;
  duelsTotal: number;
  yellowCards: number;
  redCards: number;
}

export interface GameweekInfo {
  gameweek: number;
  matchCount: number;
  date: string;
}

export interface LeagueTeam {
  teamId: number;
  teamName: string;
}

export interface LeagueSeason {
  seasonId: number;
  competitionId: number;
  label: string;
  matchCount: number;
}

export interface LeagueMatchStats {
  matchCount: number;
  playedCount: number;
  totalGoals: number;
  avgGoals: number;
  topScorer: { playerId: number; name: string; goals: number } | null;
  highestScoring: { wyscoutMatchId: number; label: string; totalGoals: number } | null;
}

export interface LeaguePlayerStatTotals {
  matchesPlayed: number;
  minutesPlayed: number;
  goals: number;
  assists: number;
  xgShot: number;
  xgAssist: number;
  shots: number;
  keyPasses: number;
  passesAccurate: number;
  passesTotal: number;
  duelsWon: number;
  duelsTotal: number;
  yellowCards: number;
  redCards: number;
}

export interface LeaguePlayerMatchEntry {
  matchId: number;
  gameweek: number | null;
  dateUtc: string | null;
  label: string | null;
  homeTeamId: number | null;
  awayTeamId: number | null;
  homeTeamName: string | null;
  awayTeamName: string | null;
  homeScore: number | null;
  awayScore: number | null;
  teamId: number;
  position: string | null;
  minutesPlayed: number;
  goals: number;
  assists: number;
  xgShot: number;
  xgAssist: number;
  shots: number;
  keyPasses: number;
  passesAccurate: number;
  passesTotal: number;
  duelsWon: number;
  duelsTotal: number;
  yellowCards: number;
  redCards: number;
}

export interface LeaguePlayerStatsResponse {
  playerId: number;
  teamId: number;
  playerName: string | null;
  shortName: string | null;
  position: string | null;
  totals: LeaguePlayerStatTotals;
  matches: LeaguePlayerMatchEntry[];
}

// League API
export const leagueAPI = {
  getMatches: async (params: {
    seasonId?: number;
    gameweek?: number;
    teamId?: number;
    hednesfordTeamId?: number;
    dateFrom?: string;
    dateTo?: string;
    minTotalGoals?: number;
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: LeagueMatch[]; total: number; page: number; limit: number; totalPages: number }> => {
    const sp = new URLSearchParams();
    if (params.seasonId) sp.append('seasonId', String(params.seasonId));
    if (params.gameweek) sp.append('gameweek', String(params.gameweek));
    if (params.teamId) sp.append('teamId', String(params.teamId));
    if (params.hednesfordTeamId) sp.append('hednesfordTeamId', String(params.hednesfordTeamId));
    if (params.dateFrom) sp.append('dateFrom', params.dateFrom);
    if (params.dateTo) sp.append('dateTo', params.dateTo);
    if (params.minTotalGoals !== undefined) sp.append('minTotalGoals', String(params.minTotalGoals));
    if (params.status) sp.append('status', params.status);
    if (params.search) sp.append('search', params.search);
    if (params.page) sp.append('page', String(params.page));
    if (params.limit) sp.append('limit', String(params.limit));
    const qs = sp.toString();
    const response = await api.get(`/league/matches${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  getMatchStats: async (params: {
    seasonId?: number;
    gameweek?: number;
    teamId?: number;
    hednesfordTeamId?: number;
    dateFrom?: string;
    dateTo?: string;
    minTotalGoals?: number;
    status?: string;
    search?: string;
  }): Promise<LeagueMatchStats> => {
    const sp = new URLSearchParams();
    if (params.seasonId) sp.append('seasonId', String(params.seasonId));
    if (params.gameweek) sp.append('gameweek', String(params.gameweek));
    if (params.teamId) sp.append('teamId', String(params.teamId));
    if (params.hednesfordTeamId) sp.append('hednesfordTeamId', String(params.hednesfordTeamId));
    if (params.dateFrom) sp.append('dateFrom', params.dateFrom);
    if (params.dateTo) sp.append('dateTo', params.dateTo);
    if (params.minTotalGoals !== undefined) sp.append('minTotalGoals', String(params.minTotalGoals));
    if (params.status) sp.append('status', params.status);
    if (params.search) sp.append('search', params.search);
    const qs = sp.toString();
    const response = await api.get(`/league/matches/stats${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  getMatchPlayerStats: async (matchId: number): Promise<{ data: LeaguePlayerStat[] }> => {
    const response = await api.get(`/league/matches/${matchId}/player-stats`);
    return response.data;
  },

  getGameweeks: async (seasonId?: number): Promise<{ data: GameweekInfo[] }> => {
    const sid = seasonId || 191784;
    const response = await api.get(`/league/gameweeks?seasonId=${sid}`);
    return response.data;
  },

  getTeams: async (seasonId?: number): Promise<{ data: LeagueTeam[] }> => {
    const sid = seasonId || 191784;
    const response = await api.get(`/league/teams?seasonId=${sid}`);
    return response.data;
  },

  getSeasons: async (): Promise<{ data: LeagueSeason[] }> => {
    const response = await api.get('/league/seasons');
    return response.data;
  },

  getPlayerStats: async (playerId: number, seasonId?: number): Promise<{ data: LeaguePlayerStatsResponse }> => {
    const sid = seasonId || 191784;
    const response = await api.get(`/league/players/${playerId}/stats?seasonId=${sid}`);
    return response.data;
  },

  syncPlayerStats: async (lookbackDays?: number, seasonId?: number): Promise<{ message: string }> => {
    const sp = new URLSearchParams();
    sp.append('seasonId', String(seasonId || 191784));
    if (lookbackDays && lookbackDays > 0) sp.append('lookbackDays', String(lookbackDays));
    const response = await api.post(`/league/sync-player-stats?${sp.toString()}`);
    return response.data;
  },

  resyncPlayerStats: async (seasonId?: number): Promise<{ message: string }> => {
    const sid = seasonId || 191784;
    const response = await api.post(`/league/resync-player-stats?seasonId=${sid}`);
    return response.data;
  },
};
